import { forwardRef, useImperativeHandle, useRef, type ComponentProps } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import FieldLabelMobile from '@/domains/mobile-web/components/FieldLabelMobile/FieldLabelMobile';

export type DateTimeMobileType = 'date' | 'time' | 'datetime-local';

export interface DateTimeMobileProps extends Omit<ComponentProps<'input'>, 'className' | 'type'> {
    label?: string;
    type?: DateTimeMobileType;
    error?: string;
    helperText?: string;
    className?: string;
    inputClassName?: string;
}

const DateTimeMobile = forwardRef<HTMLInputElement, DateTimeMobileProps>(
    (
        { label, type = 'date', error, helperText, className, inputClassName, id, name, required, disabled, ...props },
        ref
    ) => {
        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

        const inputId = id ?? name;
        const Icon = type === 'time' ? Clock : Calendar;

        const openPicker = () => {
            const el = inputRef.current;
            if (!el || disabled) return;

            if (typeof el.showPicker === 'function') {
                try {
                    el.showPicker();
                    return;
                } catch {

                }
            }
            el.focus();
        };

        return (
            <FieldLabelMobile
                label={label}
                htmlFor={inputId}
                required={required}
                error={error}
                helperText={helperText}
                className={className}
            >
                <div className="relative w-full">
                    <input
                        ref={inputRef}
                        id={inputId}
                        name={name}
                        type={type}
                        required={required}
                        disabled={disabled}
                        aria-invalid={!!error}
                        className={cn(
                            'field-mobile field-mobile-datetime pr-11',
                            error && 'field-mobile-error',
                            inputClassName
                        )}
                        {...props}
                    />
                    <button
                        type="button"
                        onClick={openPicker}
                        disabled={disabled}
                        aria-label={type === 'time' ? 'Pilih jam' : 'Pilih tanggal'}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-black disabled:opacity-50"
                    >
                        <Icon className="size-5" />
                    </button>
                </div>
            </FieldLabelMobile>
        );
    }
);

DateTimeMobile.displayName = 'DateTimeMobile';

export default DateTimeMobile;
